import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { riasecMeta } from "@/lib/riasec";
import { Card } from "@/components/ui/card";
import { Compass, ArrowRight, Sparkles } from "lucide-react";

// Compact RIASEC summary shown inside the Career Fit section of the student
// dashboard. Deliberately separate from the TSB Skill Radar — never mixes scores.
export function InterestProfileCard() {
  const [state, setState] = useState({ loading: true, available: true, latest: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [statusRes, latestRes] = await Promise.allSettled([
          api.get("/assessments/onet/status"),
          api.get("/assessments/latest"),
        ]);
        if (!alive) return;
        const available = statusRes.status === "fulfilled" ? statusRes.value.data.available : false;
        const latest = latestRes.status === "fulfilled" && !latestRes.value.data.empty ? latestRes.value.data : null;
        setState({ loading: false, available, latest });
      } catch {
        if (alive) setState({ loading: false, available: false, latest: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  const top3 = state.latest?.top3 || [];

  return (
    <div className="mb-5 rounded-2xl border border-[#005291]/15 bg-gradient-to-br from-[#f2f7fc] to-white p-5" data-testid="interest-profile-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-heading text-base font-bold text-[#003057]">
            <Compass className="h-5 w-5 text-[#005291]" /> Career Interest Profile
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Your O*NET vocational interests (RIASEC) — <span className="font-semibold">separate</span> from your Transferable Skills Benchmark.
          </p>
        </div>
        <Link to="/app/student/interests"
          className="inline-flex items-center gap-1 rounded-full bg-[#003057] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#00243f]"
          data-testid="interest-card-cta">
          {top3.length ? "View results" : "Take the assessment"} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {state.loading && <p className="mt-3 text-sm text-slate-400">Loading…</p>}

      {!state.loading && !state.available && !top3.length && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="interest-card-unavailable">
          The Career Interest Profile is temporarily unavailable. Please check back later.
        </p>
      )}

      {!state.loading && top3.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="interest-card-top3">
          {top3.map((r, i) => {
            const m = riasecMeta(r.title);
            return (
              <span key={r.code || i}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold text-white"
                style={{ backgroundColor: m.color }}>
                {i === 0 && <Sparkles className="h-3.5 w-3.5" />} {r.title}{m.tag ? ` · ${m.tag}` : ""}
              </span>
            );
          })}
        </div>
      )}

      {!state.loading && state.available && !top3.length && (
        <p className="mt-3 text-sm text-slate-500" data-testid="interest-card-empty">
          Discover the careers that match your interests with the official 30-question O*NET Interest Profiler.
        </p>
      )}
    </div>
  );
}
